import { RouteNames } from 'pages/teach-course/constants';

import { getItemTypePrefixTranslation } from 'bundles/author-course/utils/ItemTypeUtilsPrefixes';

import _t from 'i18n!nls/author-course';

export type ItemTypeAdditionalParameters = {
  isTeamAssignment?: boolean;
  useShortFormName?: boolean;
  isGraded?: boolean;
  customDisplayTypenameOverride?: string;
};

export type ItemPrefix =
  | 'Video'
  | 'Notebook'
  | 'Reading'
  | 'Discussion Prompt'
  | 'Graded Discussion Prompt'
  | 'Quiz'
  | 'Peer Review'
  | 'Programming Assignment'
  | 'Assignment'
  | 'App Item'
  | 'Graded Assignment'
  | 'Graded Team Assignment'
  | 'Practice Assignment'
  | 'Practice Team Assignment'
  | 'Teammate Review'
  | 'Ungraded Plugin'
  | 'Dialogue'
  | 'Graded Plugin'
  | 'Workspace'
  | 'Ungraded Lab'
  | 'WISEflow Item'
  | 'Placeholder'
  | 'Guided Project'
  | 'Coach Item'
  | 'Role Play';

const {
  LECTURE_AUTHOR,
  READING_AUTHOR,
  DISCUSSION_PROMPT_AUTHOR,
  GRADED_DISCUSSION_PROMPT_AUTHOR,
  QUIZ_AUTHOR,
  PEER_REVIEW_AUTHOR,
  PROGRAMMING_AUTHOR,
  LTI_AUTHOR,
  STAFF_GRADED_AUTHOR,
  TEAMMATE_REVIEW_AUTHOR,
  PLUGIN_AUTHOR,
  UNGRADED_LAB_AUTHOR,
  WISEFLOW_AUTHOR,
  PLACEHOLDER_AUTHOR,
  COACH_ITEM_AUTHOR,
} = RouteNames;

type ItemTypeConstructorParams = {
  typeName: string;
  routeName: string;
  prefix: ItemPrefix;
  getLabel?: () => string;
  getDefaultName?: () => string;
  editable?: boolean;
  graded?: boolean;
  getPrefixTranslation: () => string;
  unifySGAEnabled?: boolean;
  isTeamAssignment?: boolean;
  // `typeNameOverride` serves as an identifier for CoachItem class object specifically as of right now to return the
  // correct values based on the type of Item object being created (Role Play or Dialogue). The goal is to use an
  // identifier to determine, what kind of Coach Item class object is being created.
  typeNameOverride?: string;
};

export class ItemTypeClass {
  declare typeName: string;

  declare routeName: string;

  declare prefix: ItemPrefix;

  declare getLabel: () => string;

  declare getDefaultName: () => string;

  declare editable: boolean;

  declare graded: boolean;

  declare getPrefixTranslation: (itemTypeAdditionalParameters?: ItemTypeAdditionalParameters) => string;

  constructor({
    typeName,
    routeName,
    prefix,
    getLabel = () => '',
    getDefaultName = () => _t('New Item'),
    editable = false,
    graded = false,
    getPrefixTranslation,
  }: ItemTypeConstructorParams) {
    this.typeName = typeName;
    this.routeName = routeName;
    this.prefix = prefix;
    this.getLabel = getLabel;
    this.getDefaultName = getDefaultName;
    this.editable = editable;
    this.graded = graded;
    this.getPrefixTranslation = getPrefixTranslation;
  }

  get label() {
    return this.getLabel();
  }

  get defaultName() {
    return this.getDefaultName();
  }
}

class QuizItemType extends ItemTypeClass {
  constructor({
    typeName,
    getLabel,
    graded = false,
  }: Pick<ItemTypeConstructorParams, 'typeName' | 'getLabel' | 'graded'>) {
    super({
      getLabel,
      graded,
      typeName,
      editable: true,
      prefix: 'Quiz',
      routeName: QUIZ_AUTHOR,
      getDefaultName: () => _t('New Quiz'),
      getPrefixTranslation: () => getItemTypePrefixTranslation('Quiz'),
    });
  }
}

class PeerItemType extends ItemTypeClass {
  constructor({ typeName, graded = false }: Pick<ItemTypeConstructorParams, 'typeName' | 'graded'>) {
    super({
      graded,
      typeName,

      editable: true,
      routeName: PEER_REVIEW_AUTHOR,
      prefix: 'Peer Review',
      getLabel: () => _t('Peer Review'),
      getDefaultName: () => _t('New Peer Review'),
      getPrefixTranslation: () => getItemTypePrefixTranslation('Peer Review'),
    });
  }
}

class PlaceholderItemType extends ItemTypeClass {
  constructor({ typeName, graded = false }: Pick<ItemTypeConstructorParams, 'typeName' | 'graded'>) {
    super({
      graded,
      typeName,

      editable: true,
      routeName: PLACEHOLDER_AUTHOR,
      prefix: 'Placeholder',
      getLabel: () => _t('Deprecated Item'),
      getDefaultName: () => _t('Deprecated Item'),
      getPrefixTranslation: () => getItemTypePrefixTranslation('Placeholder'),
    });
  }
}

class GradedPeerItemType extends PeerItemType {
  constructor({ typeName }: Pick<ItemTypeConstructorParams, 'typeName'>) {
    super({ typeName, graded: true });
  }
}

class ProgrammingItemType extends ItemTypeClass {
  constructor({
    typeName,
    getLabel,
    editable = true,
    graded = false,
  }: Pick<ItemTypeConstructorParams, 'typeName' | 'getLabel' | 'editable' | 'graded'>) {
    super({
      getLabel,
      graded,
      editable,
      typeName,

      prefix: 'Programming Assignment',
      routeName: PROGRAMMING_AUTHOR,
      getDefaultName: () => _t('New Programming Assignment'),
      getPrefixTranslation: () => getItemTypePrefixTranslation('Programming Assignment'),
    });
  }
}

class LtiItemType extends ItemTypeClass {
  constructor({
    typeName,
    editable = true,
    graded = false,
  }: Pick<ItemTypeConstructorParams, 'typeName' | 'editable' | 'graded'>) {
    super({
      typeName,
      editable,
      graded,
      routeName: LTI_AUTHOR,
      getDefaultName() {
        return _t('New App Item');
      },
      prefix: 'App Item',
      getPrefixTranslation: () => getItemTypePrefixTranslation('App Item'),
    });
  }
}

/** Staff Graded Project (Team/Individual Project) has been renamed to Assignment, but we continue to use 'staffGraded'
 * as the item type name on the backend, so we are maintaining this terminology to prevent confusion
 * */
class StaffGradedProjectType extends ItemTypeClass {
  constructor({
    typeName,
    graded = true,
    isTeamAssignment = false,
  }: Pick<ItemTypeConstructorParams, 'typeName' | 'graded' | 'isTeamAssignment'>) {
    const prefix: ItemPrefix = 'Assignment';

    super({
      graded,
      typeName,

      editable: true,
      routeName: STAFF_GRADED_AUTHOR,
      prefix,
      getLabel() {
        return getItemTypePrefixTranslation(prefix);
      },
      getDefaultName() {
        return _t('Untitled');
      },
      getPrefixTranslation: (itemTypeAdditionalParameters?: ItemTypeAdditionalParameters) => {
        return getItemTypePrefixTranslation(prefix, {
          isTeamAssignment,
          isGraded: graded,
          ...itemTypeAdditionalParameters,
        });
      },
    });
  }
}

class TeammateReviewType extends ItemTypeClass {
  constructor({ typeName, graded = true }: Pick<ItemTypeConstructorParams, 'typeName' | 'graded'>) {
    super({
      graded,
      typeName,

      editable: true,
      routeName: TEAMMATE_REVIEW_AUTHOR,
      prefix: 'Teammate Review',
      getLabel() {
        return _t('Teammate Review');
      },
      getDefaultName() {
        return _t('New Teammate Review');
      },
      getPrefixTranslation: () => getItemTypePrefixTranslation('Teammate Review'),
    });
  }
}

class WidgetItemType extends ItemTypeClass {
  constructor({ typeName, graded }: Pick<ItemTypeConstructorParams, 'typeName' | 'graded'>) {
    const getLabel = () => {
      if (typeName === 'dialogue') {
        return _t('Dialogue');
      }
      return graded ? _t('Graded Plugin') : _t('Ungraded Plugin');
    };

    const getDefaultName = () => {
      if (typeName === 'dialogue') {
        return _t('New Dialogue Item');
      }

      return graded ? _t('New Graded Plugin Item') : _t('New Ungraded Plugin Item');
    };

    const getPrefix = () => {
      if (typeName === 'dialogue') {
        return 'Dialogue';
      }

      return graded ? 'Graded Plugin' : 'Ungraded Plugin';
    };

    const getPrefixTranslation = () => {
      if (typeName === 'dialogue') {
        return getItemTypePrefixTranslation('Dialogue');
      }

      return getItemTypePrefixTranslation(graded ? 'Graded Plugin' : 'Ungraded Plugin');
    };

    super({
      graded,
      typeName,

      editable: true,
      routeName: PLUGIN_AUTHOR,
      prefix: getPrefix(),
      getLabel,
      getDefaultName,
      getPrefixTranslation,
    });
  }
}

class UngradedLabItemType extends ItemTypeClass {
  constructor({ typeName, graded }: Pick<ItemTypeConstructorParams, 'typeName' | 'graded'>) {
    const getLabel = () => _t('Ungraded Lab');

    super({
      typeName,
      routeName: UNGRADED_LAB_AUTHOR,
      prefix: 'Ungraded Lab',
      getLabel,
      editable: true,
      graded,
      getDefaultName: () => _t('New Ungraded Lab'),
      getPrefixTranslation: () => getItemTypePrefixTranslation('Ungraded Lab'),
    });
  }
}

class WiseFlowItemType extends ItemTypeClass {
  constructor({ typeName, graded, editable }: Pick<ItemTypeConstructorParams, 'typeName' | 'graded' | 'editable'>) {
    const getLabel = () => _t('WISEflow Item');

    // wiseflow item type is editable if the authoring flow is enabled
    // TODO: return true once epic feature flag is rolled out
    super({
      typeName,
      routeName: WISEFLOW_AUTHOR,
      prefix: 'WISEflow Item',
      getLabel,
      editable,
      graded,
      getDefaultName: () => _t('New WISEflow Item'),
      getPrefixTranslation: () => getItemTypePrefixTranslation('WISEflow Item'),
    });
  }
}

class CoachItemType extends ItemTypeClass {
  constructor({
    typeName,
    graded,
    typeNameOverride,
  }: Pick<ItemTypeConstructorParams, 'typeName' | 'graded' | 'typeNameOverride'>) {
    const getLabel = () => {
      if (typeNameOverride === 'rolePlay') {
        return _t('Role Play');
      }
      return _t('Dialogue');
    };

    const getDefaultName = () => {
      if (typeNameOverride === 'rolePlay') {
        return _t('New Role Play');
      }
      return _t('New Dialogue');
    };

    const getPrefix = (): ItemPrefix => {
      if (typeNameOverride === 'rolePlay') {
        return 'Role Play';
      }

      return 'Dialogue';
    };

    const getPrefixTranslation = () => {
      if (typeNameOverride === 'rolePlay') {
        return getItemTypePrefixTranslation('Role Play');
      }

      return getItemTypePrefixTranslation('Dialogue');
    };

    super({
      graded,
      typeName,
      editable: true,
      routeName: COACH_ITEM_AUTHOR,
      prefix: getPrefix(),
      getLabel,
      getDefaultName,
      getPrefixTranslation,
    });
  }
}

/**
 * Possible item types in authoring.
 */
export const ItemTypes = {
  LECTURE: new ItemTypeClass({
    editable: true,
    typeName: 'lecture',
    routeName: LECTURE_AUTHOR,
    prefix: 'Video',
    getDefaultName() {
      return _t('New Video');
    },
    getPrefixTranslation: () => getItemTypePrefixTranslation('Video'),
  }),

  SUPPLEMENT: new ItemTypeClass({
    editable: true,
    typeName: 'supplement',
    routeName: READING_AUTHOR,
    prefix: 'Reading',
    getDefaultName() {
      return _t('New Reading');
    },
    getPrefixTranslation: () => getItemTypePrefixTranslation('Reading'),
  }),

  DISCUSSION_PROMPT: new ItemTypeClass({
    editable: true,
    typeName: 'discussionPrompt',
    routeName: DISCUSSION_PROMPT_AUTHOR,
    getDefaultName() {
      return _t('New Discussion Prompt');
    },
    prefix: 'Discussion Prompt',
    getPrefixTranslation: () => getItemTypePrefixTranslation('Discussion Prompt'),
  }),

  GRADED_DISCUSSION_PROMPT: new ItemTypeClass({
    typeName: 'gradedDiscussionPrompt',
    routeName: GRADED_DISCUSSION_PROMPT_AUTHOR,
    prefix: 'Graded Discussion Prompt',
    getLabel() {
      return _t('Graded Discussion Prompt');
    },
    getDefaultName() {
      return _t('New Graded Discussion Prompt');
    },
    editable: true,
    graded: true,
    getPrefixTranslation: () => getItemTypePrefixTranslation('Graded Discussion Prompt'),
  }),

  /* Quiz */
  SUMMATIVE_QUIZ: new QuizItemType({
    graded: true,
    typeName: 'exam',
    getLabel() {
      return _t('Graded Quiz');
    },
  }),

  FORMATIVE_QUIZ: new QuizItemType({
    typeName: 'quiz',
    getLabel() {
      return _t('Practice Quiz');
    },
  }),

  ACCESS_OPEN_SIGLE_PAGE: new QuizItemType({
    typeName: 'assessOpenSinglePage',
  }),

  /* Peer */
  STANDARD_PEER: new PeerItemType({ typeName: 'peer' }),

  CLOSED_PEER: new GradedPeerItemType({ typeName: 'closedPeer' }),
  GRADED_PEER: new GradedPeerItemType({ typeName: 'gradedPeer' }),
  PHASED_PEER: new GradedPeerItemType({ typeName: 'phasedPeer' }),

  /* Programming */
  PROGRAMMING: new ProgrammingItemType({
    editable: false,
    typeName: 'programming',
    getLabel() {
      return _t('Programming Assignment');
    },
  }),

  GRADED_PROGRAMMING: new ProgrammingItemType({
    graded: true,
    typeName: 'gradedProgramming',
    getLabel() {
      return _t('Graded Programming Assignment');
    },
  }),

  UNGRADED_PROGRAMMING: new ProgrammingItemType({
    typeName: 'ungradedProgramming',
    getLabel() {
      return _t('Ungraded Programming Assignment');
    },
  }),

  /* LTI */
  UNGRADED_LTI: new LtiItemType({ typeName: 'ungradedLti' }),

  GRADED_LTI: new LtiItemType({ typeName: 'gradedLti', graded: true }),

  /** WiseFlow */
  WISEFLOW: new WiseFlowItemType({
    typeName: 'wiseFlow',
    graded: true,
    editable: true,
  }),

  /** Staff Graded Project (Team/Individual Project)
   * @deprecated Use ASSIGNMENT / TEAM_ASSIGNMENT / PRACTICE_ASSIGNMENT / PRACTICE_TEAM_ASSIGNMENT instead.
   * We continue to use 'staffGraded' as the item type name on the backend, so we are maintaining this terminology to prevent confusion
   * */
  STAFF_GRADED: new StaffGradedProjectType({
    typeName: 'staffGraded',
    graded: true,
  }),

  /* Unified Graded Assignment (Individual) */
  ASSIGNMENT: new StaffGradedProjectType({
    typeName: 'staffGraded',
    graded: true,
  }),

  /* Unified Graded Assignment (Team) */
  TEAM_ASSIGNMENT: new StaffGradedProjectType({
    typeName: 'staffGraded',
    graded: true,
    isTeamAssignment: true,
  }),

  /* Unified Practice Assignment (Individual) */
  PRACTICE_ASSIGNMENT: new StaffGradedProjectType({
    typeName: 'ungradedAssignment',
    graded: false,
  }),

  /* Unified Practice Assignment (Team) */
  PRACTICE_TEAM_ASSIGNMENT: new StaffGradedProjectType({
    typeName: 'ungradedAssignment',
    graded: false,
    isTeamAssignment: true,
  }),

  /* Teammate Review (aka Compound / Atomic assessments) */
  TEAMMATE_REVIEW: new TeammateReviewType({
    typeName: 'teammateReview',
    graded: true,
  }),

  UNGRADED_PLUGIN: new WidgetItemType({
    typeName: 'ungradedWidget',
    graded: false,
  }),

  GRADED_PLUGIN: new WidgetItemType({
    typeName: 'gradedWidget',
    graded: true,
  }),

  UNGRADED_LAB: new UngradedLabItemType({
    typeName: 'ungradedLab',
    graded: false,
  }),

  // DIALOGUE ItemType is a legacy plugin/widget type that is planned to migrated and deleted.
  // JIRA - https://coursera.atlassian.net/browse/HOL-5893
  DIALOGUE: new WidgetItemType({
    typeName: 'dialogue',
    graded: false,
  }),

  COACH_ITEM_DIALOGUE: new CoachItemType({
    typeName: 'coach',
    graded: false,
    typeNameOverride: 'dialogue',
  }),

  COACH_ITEM_ROLE_PLAY: new CoachItemType({
    typeName: 'coach',
    graded: false,
    typeNameOverride: 'rolePlay',
  }),

  UNGRADED_PLACEHOLDER: new PlaceholderItemType({ typeName: 'ungradedPlaceholder', graded: false }),
  GRADED_PLACEHOLDER: new PlaceholderItemType({ typeName: 'gradedPlaceholder', graded: true }),
} as const;

export default ItemTypes as { [key: string]: ItemTypeClass };

export type ItemTypeType = (typeof ItemTypes)[keyof typeof ItemTypes];

export const {
  LECTURE,
  SUPPLEMENT,
  DISCUSSION_PROMPT,
  GRADED_DISCUSSION_PROMPT,
  SUMMATIVE_QUIZ,
  FORMATIVE_QUIZ,
  ACCESS_OPEN_SIGLE_PAGE,
  STANDARD_PEER,
  CLOSED_PEER,
  GRADED_PEER,
  PHASED_PEER,
  PROGRAMMING,
  GRADED_PROGRAMMING,
  UNGRADED_PROGRAMMING,
  UNGRADED_LTI,
  GRADED_LTI,
  /** Staff Graded Project (Team/Individual Project)
   * @deprecated Use ASSIGNMENT / TEAM_ASSIGNMENT / PRACTICE_ASSIGNMENT / PRACTICE_TEAM_ASSIGNMENT instead.
   * */
  STAFF_GRADED,
  ASSIGNMENT,
  TEAM_ASSIGNMENT,
  PRACTICE_ASSIGNMENT,
  PRACTICE_TEAM_ASSIGNMENT,
  TEAMMATE_REVIEW,
  UNGRADED_PLUGIN,
  GRADED_PLUGIN,
  UNGRADED_LAB,
  WISEFLOW,
  DIALOGUE,
  COACH_ITEM_DIALOGUE,
  COACH_ITEM_ROLE_PLAY,
  GRADED_PLACEHOLDER,
  UNGRADED_PLACEHOLDER,
} = ItemTypes;
